import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';

const FONT_URL = "https://fonts.googleapis.com/css2?family=Fraunces:wght@700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";

const PERIODS = [
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'All Time', value: 'all' },
];

function getStartDate(period) {
  const now = new Date();
  if (period === 'today') return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  if (period === 'week') {
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(now.getFullYear(), now.getMonth(), diff).toISOString();
  }
  if (period === 'month') return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  return null;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return isMobile;
}

function Home() {
  const isMobile = useIsMobile();
  const [businessId, setBusinessId] = useState(null);
  const [stats, setStats] = useState({ calls: null, leads: null, conversations: null });
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('month');
  const [avgTicket, setAvgTicket] = useState(() => {
    const stored = localStorage.getItem('replyfirst_avg_ticket');
    return stored ? Number(stored) : 0;
  });
  const [editingTicket, setEditingTicket] = useState(false);
  const [ticketInput, setTicketInput] = useState('');

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONT_URL;
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  // Get logged-in user's business_id first
  useEffect(() => {
    const fetchBusinessId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: business } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_email', user.email)
        .single();

      if (business) setBusinessId(business.id);
    };
    fetchBusinessId();
  }, []);

  // Fetch stats once we have business_id
  useEffect(() => {
    if (!businessId) return;

    const fetchData = async () => {
      setLoading(true);
      setError('');
      const start = getStartDate(period);

      const callsQ = supabase.from('conversations').select('id', { count: 'exact', head: true }).eq('business_id', businessId);
      const leadsQ = supabase.from('leads').select('id', { count: 'exact', head: true }).eq('business_id', businessId);
      const convosQ = supabase.from('conversations').select('id', { count: 'exact', head: true }).eq('business_id', businessId);

      if (start) {
        callsQ.gte('created_at', start);
        leadsQ.gte('created_at', start);
        convosQ.gte('created_at', start);
      }

      const [callsRes, leadsRes, convosRes, recentLeadsRes] = await Promise.all([
        callsQ, leadsQ, convosQ,
        supabase.from('leads').select('id, customer_name, phone_number, need, created_at').eq('business_id', businessId).order('created_at', { ascending: false }).limit(10),
      ]);

      if (callsRes.error || leadsRes.error || convosRes.error || recentLeadsRes.error) {
        setError('Unable to load dashboard data. Please try again.');
        setLoading(false);
        return;
      }

      setStats({ calls: callsRes.count ?? 0, leads: leadsRes.count ?? 0, conversations: convosRes.count ?? 0 });
      setRecentLeads(recentLeadsRes.data || []);
      setLoading(false);
    };

    fetchData();
  }, [businessId, period]);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const estimatedRevenue = avgTicket > 0 && stats.leads !== null
    ? (stats.leads * avgTicket).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : null;

  const handleSaveTicket = () => {
    const val = parseFloat(ticketInput);
    if (!isNaN(val) && val > 0) { setAvgTicket(val); localStorage.setItem('replyfirst_avg_ticket', val); }
    setEditingTicket(false);
    setTicketInput('');
  };

  const periodLabel = PERIODS.find(p => p.value === period)?.label || 'This Month';
  const pad = isMobile ? '16px' : '32px';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F4F7FF', padding: pad, boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ maxWidth: '1120px', margin: '0 auto' }}>

        <header style={{ marginBottom: isMobile ? '20px' : '32px' }}>
          <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h1 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: isMobile ? '26px' : '32px', fontWeight: 800, color: '#0C1A2E', letterSpacing: '-0.5px' }}>Overview</h1>
              {!isMobile && <p style={{ marginTop: '6px', color: '#5A6B84', fontSize: '14px' }}>Your ReplyFirst dashboard — leads captured, revenue recovered.</p>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {!isMobile && <span style={{ fontSize: '13px', color: '#5A6B84', fontWeight: 500 }}>Showing:</span>}
              <select value={period} onChange={(e) => setPeriod(e.target.value)}
                style={{ fontSize: '13px', fontWeight: 600, color: '#1A56DB', backgroundColor: '#EEF3FF', border: '1px solid #C3D2F7', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", outline: 'none' }}>
                {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>
        </header>

        {error && (
          <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '14px' }}>{error}</div>
        )}

        {/* Stat Cards */}
        <section style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: isMobile ? '12px' : '16px', marginBottom: isMobile ? '16px' : '24px' }}>
          {[
            { icon: '📞', label: 'Calls', value: stats.calls },
            { icon: '✅', label: 'Leads', value: stats.leads },
            { icon: '💬', label: 'Conversations', value: stats.conversations },
          ].map((card, i) => (
            <div key={card.label} style={{ backgroundColor: '#fff', borderRadius: '14px', padding: isMobile ? '16px' : '20px 22px', border: '1px solid #DDE3F0', boxShadow: '0 2px 8px rgba(26,86,219,0.06)', gridColumn: isMobile && i === 2 ? '1 / -1' : undefined }}>
              <div style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '8px' }}>{card.icon}</div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#5A6B84', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.label} — {periodLabel}</div>
              <div style={{ fontSize: isMobile ? '32px' : '36px', fontFamily: "'Fraunces', serif", fontWeight: 800, color: '#0C1A2E', letterSpacing: '-1px', lineHeight: 1 }}>
                {loading ? '—' : (card.value ?? 0)}
              </div>
            </div>
          ))}
        </section>

        {/* Estimated Revenue */}
        <section style={{ backgroundColor: '#0C1A2E', borderRadius: '14px', padding: isMobile ? '20px' : '24px 28px', marginBottom: isMobile ? '16px' : '24px', border: '1px solid #1A2744' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: '#7A8EA8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
            Estimated Revenue Recovered — {periodLabel}
          </div>
          {avgTicket > 0 ? (
            <>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: isMobile ? '36px' : '42px', fontWeight: 800, color: '#fff', letterSpacing: '-1px', lineHeight: 1, marginBottom: '6px' }}>
                {loading ? '—' : estimatedRevenue}
              </div>
              <div style={{ fontSize: '13px', color: '#5A6B84', marginBottom: '16px' }}>
                {!loading && `${stats.leads} leads × $${avgTicket.toLocaleString()} avg ticket`}
              </div>
            </>
          ) : (
            <div style={{ fontSize: '15px', color: '#5A6B84', marginBottom: '16px' }}>Set your average ticket price to see estimated revenue.</div>
          )}
          {editingTicket ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>$</span>
              <input type="number" value={ticketInput} onChange={(e) => setTicketInput(e.target.value)} placeholder="e.g. 800" autoFocus
                style={{ width: '100px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #DDE3F0', fontSize: '14px', fontFamily: "'Plus Jakarta Sans', sans-serif" }} />
              <button onClick={handleSaveTicket} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', backgroundColor: '#F97316', color: '#fff', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>Save</button>
              <button onClick={() => { setEditingTicket(false); setTicketInput(''); }} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #5A6B84', backgroundColor: 'transparent', color: '#9AABBE', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
            </div>
          ) : (
            <button onClick={() => { setEditingTicket(true); setTicketInput(avgTicket > 0 ? String(avgTicket) : ''); }}
              style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #3D4F66', backgroundColor: 'transparent', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
              {avgTicket > 0 ? '✏️ Edit avg ticket' : '+ Set avg ticket price'}
            </button>
          )}
        </section>

        {/* Recent Leads */}
        <section style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #DDE3F0', boxShadow: '0 2px 8px rgba(26,86,219,0.06)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #DDE3F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '18px', fontWeight: 700, color: '#0C1A2E' }}>Recent Leads</h2>
            {loading && <span style={{ fontSize: '12px', color: '#9AABBE' }}>Loading…</span>}
          </div>

          {recentLeads.length === 0 && !loading ? (
            <div style={{ padding: '24px', fontSize: '14px', color: '#5A6B84' }}>No leads captured yet.</div>
          ) : isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', backgroundColor: '#EEF2FF' }}>
              {recentLeads.map((lead) => (
                <div key={lead.id} style={{ backgroundColor: '#fff', padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: '#0C1A2E', marginBottom: '2px' }}>{lead.customer_name || 'Unknown'}</div>
                      <div style={{ fontSize: '13px', color: '#5A6B84' }}>{formatDate(lead.created_at)}</div>
                    </div>
                    {lead.phone_number && (
                      <a href={`tel:${lead.phone_number}`} style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 16px', borderRadius: '999px', backgroundColor: '#22C55E', color: '#fff', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
                        📞 Call
                      </a>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', color: '#3D4F66', marginBottom: '4px' }}>{lead.phone_number || '—'}</div>
                  {lead.need && (
                    <div style={{ fontSize: '13px', color: '#5A6B84', backgroundColor: '#F4F7FF', padding: '8px 12px', borderRadius: '8px', marginTop: '8px' }}>{lead.need}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F4F7FF' }}>
                    {['Name', 'Phone Number', 'What They Needed', 'Date', 'Call'].map((h, i) => (
                      <th key={h} style={{ textAlign: i === 4 ? 'right' : 'left', padding: '10px 16px', fontWeight: 600, color: '#5A6B84', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #DDE3F0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentLeads.map((lead) => (
                    <tr key={lead.id} style={{ borderBottom: '1px solid #EEF2FF' }}>
                      <td style={{ padding: '12px 16px', color: '#0C1A2E', fontWeight: 600 }}>{lead.customer_name || '—'}</td>
                      <td style={{ padding: '12px 16px', color: '#3D4F66' }}>{lead.phone_number || '—'}</td>
                      <td style={{ padding: '12px 16px', color: '#3D4F66', maxWidth: '320px' }}>
                        <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{lead.need || '—'}</span>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#5A6B84', whiteSpace: 'nowrap' }}>{formatDate(lead.created_at)}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        {lead.phone_number ? (
                          <a href={`tel:${lead.phone_number}`} style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 14px', borderRadius: '999px', backgroundColor: '#22C55E', color: '#fff', fontSize: '12px', fontWeight: 700, textDecoration: 'none' }}>Call</a>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#9AABBE' }}>No number</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default Home;